<template lang="pug">
.briq-price-input
  label.input-label.type-bold(:for="name" v-if="label") {{ label }} 
  briq-required-and-optional-markers(:isRequired="isRequired" :hideOptionalMarker="hideOptionalMarker" :label="label")
  BriqTooltip.ml-5(:help="help")
  .input-wrapper(
    data-test-id="price-input-wrapper"
    @click="focus(true)"
    :class="{ focused: isFocused, disabled, pending, placeholder: placeholderIsActive }"
    )
    input(
      v-model="internalValue"
      type="number"
      :name="activeName"
      :step="step"
      :placeholder="placeholder"
      min="0"
      :style="`padding-left: ${currencySymbolWidth}`"
      ref="input"
      :disabled="disabled || pending"
      @blur="blur"
      @change="change"
      v-bind="$attrs"
      )
    i(:style="`width: ${currencySymbolWidth}`") {{ currencySymbol }}
    .quantity-buttons
      .quantity-button.quantity-button-up(@click="increase" data-test-id="briq-price-input-increment")
        svg.icon
          use(xlink:href="#icon-input-plus-icon")
      .quantity-button.quantity-button-down(@click="decrease" data-test-id="briq-price-input-decrement")
        svg.icon
          use(xlink:href="#icon-input-min-icon")
  .validation-message(v-if="error") {{ error }}
</template>

<script>
import slugify from '@/helpers/slugify.js'

export default {
  name: 'BriqPriceInput',
  inheritAttrs: false,
  props: {
    name: String,
    label: String,
    hideOptionalMarker: Boolean,
    help: String,
    value: { type: Number, default: null },
    placeholder: { type: Number, default: null },
    disabled: Boolean,
    currencySymbol: { type: String, default: '-' },
    allowInputFieldToBeSubmited: Boolean,
    step: { type: Number, default: 0.01 },
    allowNegativeValues: Boolean,
    error: String,
    pending: Boolean,
  },
  data() {
    return {
      isFocused: false,
      placeholderIsActive: false,
      hasPlaceholderProp: false,
      internalValue: null,
    }
  },
  computed: {
    activeName() {
      // prevent placeholder value from being submitted
      if (this.placeholderIsActive && this.allowInputFieldToBeSubmited === false) {
        return null
      }

      return this.name
    },
    currencySymbolWidth() {
      const currencySymbolWidth = this.currencySymbol.length - this.currencySymbol.length / 2 + 2
      return `${currencySymbolWidth}em`
    },
    isRequired() {
      return this.$attrs.required
    },
  },
  methods: {
    slugify,
    setupData(value) {
      this.internalValue = value !== '' ? value : null
      this.hasPlaceholderProp = typeof this.placeholder !== 'undefined' && this.placeholder !== null
      this.placeholderIsActive = this.internalValue === null ? true : false
    },
    change() {
      this.placeholderIsActive = false
    },
    focus(shouldFocus) {
      if (this.disabled || this.pending) return
      if (shouldFocus) {
        this.placeholderIsActive = false
        this.$refs['input'].focus()
      } else {
        this.maybeCheckPlaceholder()
      }
      if (this.internalValue === null && shouldFocus) this.internalValue = this.placeholder
      this.isFocused = shouldFocus
    },
    increase() {
      if (this.disabled || this.pending) return
      const oldValue = parseFloat(this.internalValue) || 0
      const newValue = oldValue + this.step
      this.setNewValue(newValue)
    },
    decrease() {
      if (this.disabled || this.pending) return
      const oldValue = parseFloat(this.internalValue) || 0
      const newValue = oldValue - this.step
      this.setNewValue(newValue)
    },
    setNewValue(newValue) {
      this.placeholderIsActive = false
      const roundedValue =
        newValue < 0 && !this.allowNegativeValues ? 0 : Math.round(100 * newValue) / 100
      this.internalValue = roundedValue
    },
    maybeCheckPlaceholder() {
      const shouldCheckPlaceholder =
        typeof this.internalValue === 'undefined' ||
        this.internalValue === '' ||
        this.internalValue === null
      if (shouldCheckPlaceholder) this.placeholderIsActive = this.hasPlaceholderProp
      else this.placeholderIsActive = false
    },
    blur() {
      if (!this.disabled && !this.pending) this.$emit('blur')
      this.focus(false)
    },
  },
  watch: {
    internalValue(newVal) {
      const isNotEmpty = number => number !== '' && number !== null
      this.$emit('input', isNotEmpty(newVal) ? Number(newVal) : null)
    },
    value: {
      handler(newVal) {
        this.setupData(newVal)
        this.maybeCheckPlaceholder()
      },
    },
  },
  mounted() {
    this.setupData(this.value)
  },
}
</script>

<style scoped lang="stylus">
quantity-button-width = 1.5em

.input-wrapper
  position relative
  cursor text
  transition border-color 0.1s ease-in-out
  background-color #fff

  &:hover
    border-color #009BFF

  // &:focus-within, // not supported by IE or Edge
  &.focused
    outline 0
    border-color #009BFF
    box-shadow 0 2px 4px 0 rgba(0, 0, 0, 0.15)

  &.pending
    cursor wait

i
  position absolute
  display block
  top 0px
  left 0px
  text-align center
  font-style normal
  padding 7px 9px
  background-color #F1F3F6
  border-right 1px solid #ebebeb

input
  border none
  width 100%

  &:focus
    outline none

  &::placeholder
    color #c1c1c1

  // hide shadow dom quantity buttons
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button
    -webkit-appearance none
    margin 0

  -moz-appearance textfield

.quantity-buttons
  .quantity-button
    position absolute
    display flex
    justify-content center
    align-items center
    top calc(50% - 8px)
    width 16px
    height 16px
    cursor pointer
    -webkit-user-select none
    -moz-user-select none
    -ms-user-select none
    -o-user-select none
    user-select none
    color #c1c1c1
    transition color 0.1s ease-in-out

    svg.icon
      width 11px
      height 11px

  .quantity-button-up
    right 0.5em + quantity-button-width

  .quantity-button-down
    right 0.5em

.input-wrapper
  border-radius 3px
  border 1px solid #ececec
  padding 7px
  line-height 24px

.input-wrapper:hover
  .quantity-button
    color black

.input-wrapper.disabled, .input-wrapper.disabled > input, .input-wrapper.disabled > i
  background-color #eee
  cursor not-allowed

  &:hover
    border-color #ececec
    .quantity-button
      color #c1c1c1
      cursor not-allowed

.input-wrapper
  &.pending
    cursor wait

    .quantity-buttons > .quantity-button
      cursor wait
      color #c1c1c1

    &:active
      box-shadow none

  &.pending > input, &.pending > i
    cursor wait

.input-wrapper.disabled,
.input-wrapper.disabled > input,
.input-wrapper.disabled > i,
.input-wrapper.pending,
.input-wrapper.pending > input,
.input-wrapper.pending > i,
.input-wrapper.placeholder > input,
.input-wrapper.placeholder > i
  color #c1c1c1
</style>
